import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navbar } from '@/components/Navbar';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';
import { format, isToday, isYesterday, isSameDay } from 'date-fns';
import { fr as frLocale, enUS } from 'date-fns/locale';
import { ArrowLeft, Loader2, MessagesSquare, Search, Send } from 'lucide-react';
import {
  fetchContacts,
  subscribeToConversations,
  sendDirectMessage,
  markMessagesRead,
  type MessagingContact,
  type DirectMessage,
  type ContactRole,
} from '@/services/messagingService';

const DICT = {
  fr: {
    title: 'Messagerie', search: 'Rechercher un contact…', all: 'Tous', student: 'Élève', teacher: 'Professeur',
    school: 'École', coach: 'Coach', students: 'Élèves', teachers: 'Professeurs', noContacts: 'Aucun contact disponible.',
    noMessages: 'Aucun message pour le moment. Écrivez le premier !', pick: 'Sélectionnez un contact pour démarrer une conversation.',
    placeholder: 'Écrire un message…', hint: 'Entrée pour envoyer · Maj+Entrée pour un retour à la ligne',
    today: "Aujourd'hui", yesterday: 'Hier', error: "Impossible d'envoyer le message.", you: 'Vous', unknown: 'Contact',
  },
  en: {
    title: 'Messages', search: 'Search a contact…', all: 'All', student: 'Student', teacher: 'Teacher',
    school: 'School', coach: 'Coach', students: 'Students', teachers: 'Teachers', noContacts: 'No contacts available.',
    noMessages: 'No messages yet. Write the first one!', pick: 'Select a contact to start a conversation.',
    placeholder: 'Write a message…', hint: 'Enter to send · Shift+Enter for a new line',
    today: 'Today', yesterday: 'Yesterday', error: 'Could not send the message.', you: 'You', unknown: 'Contact',
  },
};

const initials = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || '?';

type Filter = 'all' | ContactRole;

const Messages = () => {
  const { user, token } = useAuth();
  const { locale } = useLanguage() as any;
  const L = String(locale || 'fr').startsWith('fr') ? DICT.fr : DICT.en;
  const dfLocale = String(locale || 'fr').startsWith('fr') ? frLocale : enUS;

  const [contacts, setContacts] = useState<MessagingContact[]>([]);
  const [loadingContacts, setLoadingContacts] = useState(true);
  const [messages, setMessages] = useState<DirectMessage[]>([]);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  const me = (user?.email || '').toLowerCase();

  useEffect(() => {
    if (!user || !token) return;
    setLoadingContacts(true);
    fetchContacts(user, token)
      .then(setContacts)
      .finally(() => setLoadingContacts(false));
  }, [user?.userId, token]);

  useEffect(() => {
    if (!user?.email) return;
    return subscribeToConversations(user.email, user.userId ? String(user.userId) : undefined, setMessages);
  }, [user?.email, user?.userId]);

  // Conversations indexed by the other participant
  const threads = useMemo(() => {
    const map = new Map<string, { last: DirectMessage; unread: number; items: DirectMessage[]; name?: string }>();
    messages.forEach((m) => {
      const other = m.from === me ? m.to : m.from;
      const t = map.get(other) || { last: m, unread: 0, items: [] as DirectMessage[] };
      t.items.push(m);
      t.last = m;
      if (m.from !== me && !m.readBy.includes(me)) t.unread++;
      if (m.from !== me && m.senderName) t.name = m.senderName;
      map.set(other, t);
    });
    return map;
  }, [messages, me]);

  // Contacts + people who wrote to us but aren't in the list
  const allContacts = useMemo(() => {
    const list = [...contacts];
    threads.forEach((t, key) => {
      if (!list.some((c) => c.key === key)) {
        list.push({ key, email: key, name: t.name || key, role: 'student' });
      }
    });
    return list;
  }, [contacts, threads]);

  const availableRoles = useMemo(
    () => Array.from(new Set(allContacts.map((c) => c.role))),
    [allContacts]
  );

  const visibleContacts = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allContacts
      .filter((c) => filter === 'all' || c.role === filter)
      .filter((c) => !q || c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q))
      .sort((a, b) => {
        const ta = threads.get(a.key)?.last.createdAt.getTime() || 0;
        const tb = threads.get(b.key)?.last.createdAt.getTime() || 0;
        if (ta !== tb) return tb - ta;
        return a.name.localeCompare(b.name);
      });
  }, [allContacts, filter, search, threads]);

  const active = allContacts.find((c) => c.key === activeKey) || null;
  const activeThread = activeKey ? threads.get(activeKey) : undefined;

  // Mark as read when opening / receiving
  useEffect(() => {
    if (!activeThread || !user?.email) return;
    const unread = activeThread.items.filter((m) => m.from !== me && !m.readBy.includes(me)).map((m) => m.id);
    if (unread.length) markMessagesRead(unread, user.email);
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeThread?.items.length, activeKey]);

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || !active || !user) return;
    setSending(true);
    try {
      await sendDirectMessage({
        fromEmail: user.email,
        fromName: [user.firstname, user.lastname].filter(Boolean).join(' ') || user.school_name || user.email,
        fromUserId: user.userId ? String(user.userId) : undefined,
        to: active,
        text,
      });
      setDraft('');
    } catch (e) {
      toast({ title: L.error, variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  const roleLabel = (r: ContactRole) => L[r];
  const timeLabel = (d: Date) =>
    isToday(d) ? format(d, 'HH:mm') : isYesterday(d) ? L.yesterday : format(d, 'dd/MM/yy');
  const dayLabel = (d: Date) =>
    isToday(d) ? L.today : isYesterday(d) ? L.yesterday : format(d, 'EEEE d MMMM yyyy', { locale: dfLocale });

  const filterTabs: { id: Filter; label: string }[] = [
    { id: 'all', label: L.all },
    ...(availableRoles.includes('student') ? [{ id: 'student' as Filter, label: L.students }] : []),
    ...(availableRoles.includes('teacher') ? [{ id: 'teacher' as Filter, label: L.teachers }] : []),
    ...(availableRoles.includes('school') ? [{ id: 'school' as Filter, label: L.school }] : []),
    ...(availableRoles.includes('coach') ? [{ id: 'coach' as Filter, label: L.coach }] : []),
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      <main className="flex-1 container pt-20 mt-5 pb-4 md:pb-6">
        <div className="h-[calc(100dvh-7rem)] md:h-[calc(100dvh-7.5rem)] max-md:h-[calc(100dvh-12rem)] flex rounded-2xl border border-border bg-card overflow-hidden shadow-sm">
          {/* Contacts */}
          <aside className={cn('w-full md:w-80 lg:w-96 border-r border-border flex flex-col', active && 'max-md:hidden')}>
            <div className="p-4 space-y-3 border-b border-border">
              <div className="flex items-center gap-2">
                <MessagesSquare className="h-5 w-5 text-primary" />
                <h1 className="text-lg font-semibold">{L.title}</h1>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={L.search} className="pl-9 rounded-full bg-muted/50 border-transparent" />
              </div>
              {filterTabs.length > 2 && (
                <div className="flex gap-1.5 overflow-x-auto">
                  {filterTabs.map((f) => (
                    <button
                      key={f.id}
                      onClick={() => setFilter(f.id)}
                      className={cn(
                        'px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors',
                        filter === f.id ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'
                      )}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex-1 overflow-y-auto">
              {loadingContacts ? (
                <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
              ) : visibleContacts.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-10 px-4">{L.noContacts}</p>
              ) : (
                visibleContacts.map((c) => {
                  const t = threads.get(c.key);
                  return (
                    <button
                      key={c.key}
                      onClick={() => setActiveKey(c.key)}
                      className={cn(
                        'w-full flex items-center gap-3 px-4 py-3 text-left transition-colors border-b border-border/50',
                        activeKey === c.key ? 'bg-primary/10' : 'hover:bg-muted/60'
                      )}
                    >
                      <Avatar className="h-11 w-11 shrink-0">
                        {c.imageUrl && <AvatarImage src={c.imageUrl} />}
                        <AvatarFallback className="bg-primary/15 text-primary text-sm font-semibold">{initials(c.name)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className={cn('truncate text-sm', t?.unread ? 'font-semibold' : 'font-medium')}>{c.name}</span>
                          {t && <span className="text-[11px] text-muted-foreground shrink-0">{timeLabel(t.last.createdAt)}</span>}
                        </div>
                        <div className="flex items-center justify-between gap-2 mt-0.5">
                          <span className="truncate text-xs text-muted-foreground">
                            {t ? `${t.last.from === me ? L.you + ' : ' : ''}${t.last.text}` : roleLabel(c.role)}
                          </span>
                          {t?.unread ? (
                            <span className="shrink-0 min-w-5 h-5 px-1.5 rounded-full bg-primary text-primary-foreground text-[11px] font-semibold flex items-center justify-center">{t.unread}</span>
                          ) : t ? (
                            <Badge variant="secondary" className="shrink-0 text-[10px] px-1.5 py-0 font-normal">{roleLabel(c.role)}</Badge>
                          ) : null}
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </aside>

          {/* Conversation */}
          <section className={cn('flex-1 flex flex-col min-w-0', !active && 'max-md:hidden')}>
            {!active ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center p-8 text-muted-foreground">
                <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <MessagesSquare className="h-8 w-8 text-primary" />
                </div>
                <p className="text-sm max-w-xs">{L.pick}</p>
              </div>
            ) : (
              <>
                <header className="flex items-center gap-3 px-4 py-3 border-b border-border">
                  <Button variant="ghost" size="icon" className="md:hidden -ml-2" onClick={() => setActiveKey(null)}>
                    <ArrowLeft className="h-5 w-5" />
                  </Button>
                  <Avatar className="h-10 w-10">
                    {active.imageUrl && <AvatarImage src={active.imageUrl} />}
                    <AvatarFallback className="bg-primary/15 text-primary text-sm font-semibold">{initials(active.name)}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <p className="font-semibold truncate">{active.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{roleLabel(active.role)} · {active.email}</p>
                  </div>
                </header>

                <div className="flex-1 overflow-y-auto px-4 py-4 bg-muted/30">
                  {!activeThread?.items.length ? (
                    <p className="text-sm text-muted-foreground text-center py-10">{L.noMessages}</p>
                  ) : (
                    activeThread.items.map((m, i) => {
                      const mine = m.from === me;
                      const prev = activeThread.items[i - 1];
                      const newDay = !prev || !isSameDay(prev.createdAt, m.createdAt);
                      return (
                        <React.Fragment key={m.id}>
                          {newDay && (
                            <div className="flex justify-center my-3">
                              <span className="text-[11px] px-3 py-1 rounded-full bg-background border border-border text-muted-foreground capitalize">{dayLabel(m.createdAt)}</span>
                            </div>
                          )}
                          <div className={cn('flex mb-1.5', mine ? 'justify-end' : 'justify-start')}>
                            <div
                              className={cn(
                                'max-w-[80%] md:max-w-[65%] rounded-2xl px-3.5 py-2 shadow-sm',
                                mine ? 'bg-primary text-primary-foreground rounded-br-md' : 'bg-card border border-border rounded-bl-md'
                              )}
                            >
                              {m.legacy && m.subject && <p className="text-xs font-semibold mb-0.5 opacity-90">{m.subject}</p>}
                              <p className="text-sm whitespace-pre-wrap break-words">{m.text}</p>
                              <p className={cn('text-[10px] mt-1 text-right', mine ? 'text-primary-foreground/70' : 'text-muted-foreground')}>
                                {format(m.createdAt, 'HH:mm')}
                              </p>
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    })
                  )}
                  <div ref={endRef} />
                </div>

                <div className="border-t border-border p-3">
                  <div className="flex items-end gap-2">
                    <Textarea
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSend();
                        }
                      }}
                      placeholder={L.placeholder}
                      rows={1}
                      className="min-h-[44px] max-h-32 resize-none rounded-2xl bg-muted/50"
                    />
                    <Button size="icon" className="h-11 w-11 rounded-full shrink-0" onClick={handleSend} disabled={sending || !draft.trim()}>
                      {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    </Button>
                  </div>
                  <p className="hidden md:block text-[11px] text-muted-foreground mt-1.5 px-1">{L.hint}</p>
                </div>
              </>
            )}
          </section>
        </div>
      </main>
    </div>
  );
};

export default Messages;
